import { DelopayError } from './error';
import type { DownloadProgressEvent, RequestFn, RequestOptions } from './client';
import type {
  ExportFormat,
  ExportOptions,
  ExportPresentation,
  ExportProgress,
  ExportTransferOptions,
} from './types';
import { CsvRecordCounter, JsonArrayRecordCounter } from './recordCounter';
import type { RecordCounter } from './recordCounter';

/** The header a streamed export announces its record count in. */
const RECORD_COUNT_HEADER = 'x-delopay-record-count';

/** The header a PDF export's words travel in. */
export const PRESENTATION_HEADER = 'X-Delopay-Export-Presentation';

/**
 * Longest encoded presentation the server reads. A longer one is refused
 * there with `IR_06`; checked here first, so the caller hears it before a
 * request is sent and without a proxy's opinion of a long header in between.
 */
export const MAX_PRESENTATION_HEADER = 6 * 1024;

/**
 * `presentation` as the header carries it: base64url, unpadded, of its UTF-8
 * JSON. Rejects with `EXPORT_PRESENTATION_TOO_LARGE` past
 * {@link MAX_PRESENTATION_HEADER}.
 */
export function encodePresentation(presentation: ExportPresentation): string {
  const bytes = new TextEncoder().encode(JSON.stringify(presentation));
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  const encoded = btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  if (encoded.length > MAX_PRESENTATION_HEADER) {
    throw new DelopayError(
      `Export presentation is ${encoded.length} bytes encoded; the server reads at most ${MAX_PRESENTATION_HEADER}`,
      {
        status: 0,
        code: 'EXPORT_PRESENTATION_TOO_LARGE',
        type: 'invalid_request',
        data: { encoded_length: encoded.length, max_length: MAX_PRESENTATION_HEADER },
      },
    );
  }
  return encoded;
}

/**
 * Where a `json` export keeps its rows, and its own count of them.
 *
 * Every list export answers `{"records":[…],"record_count":N,"filters":{…}}`;
 * the disputes workspace answers `{"data":[…],"total_count":N}`.
 */
export interface ExportEnvelopeShape {
  rows: string;
  count: string;
}

/** `{"records":[…],"record_count":N,"filters":{…}}` — every list export. */
export const LIST_ENVELOPE: ExportEnvelopeShape = { rows: 'records', count: 'record_count' };

/** `{"data":[…],"total_count":N}` — the disputes workspace export. */
export const WORKSPACE_ENVELOPE: ExportEnvelopeShape = { rows: 'data', count: 'total_count' };

/** One export request, as the resource method built it. */
export interface ExportCall {
  method: string;
  path: string;
  /**
   * The request without its transfer options: filters, headers (`Accept`
   * included) and `responseType`.
   */
  request: RequestOptions;
  format: ExportFormat;
  /** The `json` envelope. Defaults to {@link LIST_ENVELOPE}. */
  envelope?: ExportEnvelopeShape;
}

/**
 * `X-Delopay-Record-Count` as a count, or `null` when there is none.
 *
 * Only a plain run of digits is a count. Anything else — a value a proxy
 * mangled, two values folded into one — is treated as no announcement rather
 * than guessed at.
 */
function parseRecordCount(header: string | null): number | null {
  if (header === null) return null;
  const trimmed = header.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return Number.isSafeInteger(value) ? value : null;
}

function counterFor(format: ExportFormat): RecordCounter | null {
  if (format === 'csv') return new CsvRecordCounter();
  if (format === 'json') return new JsonArrayRecordCounter();
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** `" of the N announced"`, or nothing when the server announced no count. */
function announced(expected: number | null): string {
  return expected === null ? '' : ` of the ${expected} announced`;
}

function incomplete(
  message: string,
  expected: number | null,
  received: number,
  requestId: string | undefined,
): DelopayError {
  return new DelopayError(message, {
    status: 0,
    code: 'EXPORT_INCOMPLETE',
    type: 'export_incomplete',
    ...(requestId !== undefined ? { requestId } : {}),
    data: { expected_records: expected, received_records: received },
  });
}

/**
 * Run one export: send it, report its progress, and hold the finished body to
 * the size the server announced before handing it back.
 *
 * The check is the point. These are finance files, and a body that ends short
 * of `X-Delopay-Record-Count` is a file missing rows that would otherwise open,
 * sum and reconcile without a word. It is refused with `EXPORT_INCOMPLETE`
 * rather than returned. For `json` the envelope's own count is held to its
 * rows as well, and a body that is not a whole JSON document is incomplete by
 * definition.
 *
 * `onProgress` hears `preparing` synchronously, before anything is sent — the
 * caller's `async` method runs this far on the call itself.
 */
export async function downloadExport<T>(
  request: RequestFn,
  call: ExportCall,
  options: ExportTransferOptions | undefined,
): Promise<T> {
  const { format } = call;
  const envelope = call.envelope ?? LIST_ENVELOPE;
  const onProgress = options?.onProgress;

  let latest: ExportProgress = {
    phase: 'preparing',
    receivedBytes: 0,
    totalBytes: null,
    receivedRecords: 0,
    expectedRecords: null,
  };
  // What the current attempt's headers announced, what its body has
  // delivered, and whether the caller's own listener threw. Kept on an object
  // because the listeners below write it.
  const seen: {
    attempt: number;
    counter: RecordCounter | null;
    expected: number | null;
    requestId: string | undefined;
    listenerError: { error: unknown } | null;
  } = { attempt: -1, counter: null, expected: null, requestId: undefined, listenerError: null };

  // A copy each time, so a listener that keeps the snapshots it is given
  // keeps what it was told. What it throws is remembered so it reaches the
  // caller as itself: a `SyntaxError` in a progress bar is not a body that
  // failed to parse.
  const report = (progress: ExportProgress) => {
    latest = progress;
    if (!onProgress) return;
    try {
      onProgress({ ...progress });
    } catch (error) {
      seen.listenerError = { error };
      throw error;
    }
  };
  report(latest);

  const onDownloadProgress = (event: DownloadProgressEvent) => {
    if (event.attempt !== seen.attempt) {
      // The headers of a new attempt: a retry starts the body over, so the
      // count does too.
      seen.attempt = event.attempt;
      seen.counter = counterFor(format);
      // A PDF is not counted, so an announced count could only ever fail it.
      seen.expected =
        format === 'pdf' ? null : parseRecordCount(event.headers.get(RECORD_COUNT_HEADER));
      seen.requestId =
        event.headers.get('x-request-id') ?? event.headers.get('x-trace-id') ?? undefined;
    }
    if (event.chunk.byteLength > 0) seen.counter?.push(event.chunk);
    report({
      phase: 'receiving',
      receivedBytes: event.loaded,
      totalBytes: event.total,
      receivedRecords: seen.counter?.records ?? 0,
      expectedRecords: seen.expected,
    });
  };

  let result: unknown;
  try {
    result = await request<unknown>(call.method, call.path, {
      ...call.request,
      ...(options?.signal !== undefined ? { signal: options.signal } : {}),
      ...(options?.timeout !== undefined ? { timeout: options.timeout } : {}),
      onDownloadProgress,
    });
  } catch (err) {
    if (seen.listenerError !== null && err === seen.listenerError.error) throw err;
    // The body arrived whole as far as the connection could tell, but it does
    // not parse: it stopped part-way through the document.
    if (format === 'json' && seen.attempt >= 0 && err instanceof SyntaxError) {
      const received = seen.counter?.records ?? 0;
      throw incomplete(
        seen.expected === null
          ? `Export incomplete: the body ended part-way through the JSON document, after ${received} records`
          : `Export incomplete: the server announced ${seen.expected} records, and the body ended part-way through the JSON document after ${received}`,
        seen.expected,
        received,
        seen.requestId,
      );
    }
    throw err;
  }

  const { expected, requestId } = seen;
  let received = 0;
  if (format === 'json') {
    // Only the export's own envelope is a JSON export. Anything else — an
    // empty 200, which parses to nothing — delivered no file, and a count of
    // zero records must not be read as a complete empty one.
    const rows = isRecord(result) ? result[envelope.rows] : undefined;
    if (!Array.isArray(rows)) {
      throw incomplete(
        `Export incomplete: the body is not an export envelope with a "${envelope.rows}" array, so it carries 0 records${announced(expected)}`,
        expected,
        0,
        requestId,
      );
    }
    received = rows.length;
    const declared = isRecord(result) ? result[envelope.count] : undefined;
    if (typeof declared === 'number' && declared !== rows.length) {
      throw incomplete(
        `Export incomplete: the file's ${envelope.count} is ${declared}, but it carries ${rows.length} records`,
        declared,
        rows.length,
        requestId,
      );
    }
  } else if (format === 'csv') {
    const counter = seen.counter;
    received = counter?.finish() ?? 0;
    // Checked whether or not a count was announced: every line of an export
    // body ends in a newline, so one that ends anywhere else was cut off.
    if (!counter?.complete) {
      throw incomplete(
        `Export incomplete: the CSV body ended part-way through a line, after ${received} complete records${announced(expected)}`,
        expected,
        received,
        requestId,
      );
    }
  }

  if (expected !== null && received !== expected) {
    throw incomplete(
      `Export incomplete: the server announced ${expected} records, and the body carried ${received}`,
      expected,
      received,
      requestId,
    );
  }

  report({
    phase: 'done',
    receivedBytes: latest.receivedBytes,
    totalBytes: latest.totalBytes,
    receivedRecords: received,
    expectedRecords: expected,
  });
  return result as T;
}

/** The media type each format is asked for by name. */
const ACCEPT: Record<ExportFormat, string> = {
  csv: 'text/csv',
  json: 'application/json',
  pdf: 'application/pdf',
};

/**
 * Headers and response handling for one export.
 *
 * The format is negotiated with `Accept`, never a request field. The API's
 * reason is that an export reuses its list's filter model as literally the same
 * type, and several of those are `deny_unknown_fields` — so a `format` key in
 * the query or body is a 400, not an option. That holds for the transactions
 * export too, whose body is the payments list filter.
 *
 * `csv` and `pdf` are not JSON, and the client parses a body as JSON unless it
 * is told otherwise, so a missing `responseType` would be a `SyntaxError` on
 * the first comma rather than a wrong type.
 */
export function negotiated(
  options: RequestOptions,
  format: ExportFormat,
  presentation?: ExportPresentation,
): RequestOptions {
  const headers: Record<string, string> = { ...options.headers, Accept: ACCEPT[format] };
  // Only a PDF prints words; a CSV or JSON file is sent none.
  if (format === 'pdf' && presentation !== undefined) {
    headers[PRESENTATION_HEADER] = encodePresentation(presentation);
  }
  return format === 'json'
    ? { ...options, headers }
    : { ...options, headers, responseType: 'blob' };
}

/**
 * Send one list export in the format `options` asks for — `json` unless told
 * otherwise — with its progress and its record-count check.
 *
 * `base` is the request as the list would send it: its filters in `body` or
 * `query`, and any header of its own. The format's `Accept` and
 * `responseType` are added here.
 */
export function runExport<T>(
  request: RequestFn,
  method: string,
  path: string,
  base: RequestOptions,
  options: ExportOptions | undefined,
): Promise<T> {
  const format = options?.format ?? 'json';
  return downloadExport<T>(
    request,
    { method, path, request: negotiated(base, format, options?.presentation), format },
    options,
  );
}
